import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MediaFilterState, MediaItem, MediaUploadProgress } from '../types';

interface MediaManagerState {
  media: MediaItem[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  hasNextPage: boolean;
  currentPage: number;
  filters: MediaFilterState;
  uploadProgress: Record<string, MediaUploadProgress>;
  pollingMediaIds: string[];
}

const initialState: MediaManagerState = {
  media: [],
  totalCount: 0,
  loading: false,
  error: null,
  hasNextPage: false,
  currentPage: 0,
  filters: {
    mediaType: 'all',
    identityId: undefined,
    sortBy: 'created_date',
    sortDirection: 'desc',
    searchQuery: '',
  },
  uploadProgress: {},
  pollingMediaIds: [],
};

const mediaManagerSlice = createSlice({
  name: 'mediaManager',
  initialState,
  reducers: {
    // Media list management
    setMedia: (state, action: PayloadAction<MediaItem[]>) => {
      state.media = action.payload;
    },
    addMedia: (state, action: PayloadAction<MediaItem>) => {
      state.media.unshift(action.payload);
      state.totalCount += 1;
    },
    updateMedia: (
      state,
      action: PayloadAction<{ mediaId: string; updates: Partial<MediaItem> }>
    ) => {
      const { mediaId, updates } = action.payload;
      const index = state.media.findIndex((item) => item.media_id === mediaId);
      if (index !== -1) {
        state.media[index] = { ...state.media[index], ...updates };
      }
    },
    removeMedia: (state, action: PayloadAction<string>) => {
      const mediaId = action.payload;
      state.media = state.media.filter((item) => item.media_id !== mediaId);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
    appendMedia: (state, action: PayloadAction<MediaItem[]>) => {
      state.media.push(...action.payload);
    },

    // Metadata management
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setHasNextPage: (state, action: PayloadAction<boolean>) => {
      state.hasNextPage = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    // Filter management
    setFilters: (state, action: PayloadAction<Partial<MediaFilterState>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setMediaTypeFilter: (state, action: PayloadAction<MediaFilterState['mediaType']>) => {
      state.filters.mediaType = action.payload;
    },
    setIdentityFilter: (state, action: PayloadAction<string | undefined>) => {
      state.filters.identityId = action.payload;
    },
    setSortBy: (state, action: PayloadAction<MediaFilterState['sortBy']>) => {
      state.filters.sortBy = action.payload;
    },
    setSortDirection: (
      state,
      action: PayloadAction<MediaFilterState['sortDirection']>
    ) => {
      state.filters.sortDirection = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.searchQuery = action.payload;
    },

    // Upload progress management
    setUploadProgress: (
      state,
      action: PayloadAction<{ fileName: string; progress: MediaUploadProgress }>
    ) => {
      const { fileName, progress } = action.payload;
      state.uploadProgress[fileName] = progress;
    },
    updateUploadProgress: (
      state,
      action: PayloadAction<{ fileName: string; updates: Partial<MediaUploadProgress> }>
    ) => {
      const { fileName, updates } = action.payload;
      if (state.uploadProgress[fileName]) {
        state.uploadProgress[fileName] = {
          ...state.uploadProgress[fileName],
          ...updates,
        };
      }
    },
    removeUploadProgress: (state, action: PayloadAction<string>) => {
      const fileName = action.payload;
      delete state.uploadProgress[fileName];
    },
    clearUploadProgress: (state) => {
      state.uploadProgress = {};
    },

    // Polling management
    addPollingMedia: (state, action: PayloadAction<string>) => {
      const mediaId = action.payload;
      if (!state.pollingMediaIds.includes(mediaId)) {
        state.pollingMediaIds.push(mediaId);
      }
    },
    removePollingMedia: (state, action: PayloadAction<string>) => {
      const mediaId = action.payload;
      state.pollingMediaIds = state.pollingMediaIds.filter((id) => id !== mediaId);
    },
    clearPollingMedia: (state) => {
      state.pollingMediaIds = [];
    },

    // Reset state
    resetMediaManager: () => initialState,
  },
});

export const {
  setMedia,
  addMedia,
  updateMedia,
  removeMedia,
  appendMedia,
  setTotalCount,
  setLoading,
  setError,
  setHasNextPage,
  setCurrentPage,
  setFilters,
  resetFilters,
  setMediaTypeFilter,
  setIdentityFilter,
  setSortBy,
  setSortDirection,
  setSearchQuery,
  setUploadProgress,
  updateUploadProgress,
  removeUploadProgress,
  clearUploadProgress,
  addPollingMedia,
  removePollingMedia,
  clearPollingMedia,
  resetMediaManager,
} = mediaManagerSlice.actions;

export default mediaManagerSlice.reducer;
